import React, { useState } from "react";
import styled from "styled-components";
import { Button } from "@mui/material";
import { Trans } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVolume } from "@duocards/pro-regular-svg-icons";
import { silenceTts } from "queries/tts/ttsTools";
import { playAudio } from "../../queries/tts/playAudio";
import { useTtsQuery } from "../../queries/ttsQuery";

export const AudioButton = styled(Button)`
  padding: 4px 6px;
  background: transparent;
  color: ${({ theme }) => theme.duo.color.primary};
  min-width: auto;
  display: flex;
  font-size: 12px;
  .MuiButton-startIcon {
    margin-right: 4px;
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

export interface TtsAudioConfig {
  type: "tts";
  text: string;
  lang: string;
}

export interface Base64AudioConfig {
  type: "base64";
  audio: string;
}

export type AudioConfig = TtsAudioConfig | Base64AudioConfig;

export interface AudioPlayButtonProps {
  text: string;
  audioConfig: AudioConfig;
}

// Type guards
export const isTtsAudio = (config: AudioConfig): config is TtsAudioConfig => {
  return config.type === "tts";
};

export const isBase64Audio = (config: AudioConfig): config is Base64AudioConfig => {
  return config.type === "base64";
};

const AudioPlayButton: React.FC<AudioPlayButtonProps> = ({ text, audioConfig }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const tts = useTtsQuery(isTtsAudio(audioConfig) ? audioConfig.lang : undefined);

  const handlePlay = (e) => {
    e.stopPropagation();
    if (!isPlaying) {
      silenceTts();
      const onStarted = () => setIsPlaying(true);
      const onEnded = () => setIsPlaying(false);

      if (isTtsAudio(audioConfig)) {
        tts?.({
          text: audioConfig.text,
          onStarted,
          onEnded
        });
      } else if (isBase64Audio(audioConfig)) {
        playAudio({
          src: audioConfig.audio,
          onStarted,
          onEnded
        });
      }
    }
  };

  return (
    <AudioButton
      onClick={handlePlay}
      variant="text"
      size="small"
      startIcon={<FontAwesomeIcon icon={faVolume} beatFade={isPlaying} />}
    >
      <Trans>{text}</Trans>
    </AudioButton>
  );
};

export default AudioPlayButton;
